/**
 * Dialog state for the Database Blueprint Setup wizard.
 *
 * The dialog is mounted globally (see GlobalDatabaseBlueprintSetupDialog) and runs
 * in one of two modes:
 *   - `new`     — first-time blueprint creation. `source` is required; the user
 *                 picks a folder, the dialog calls `database-blueprint-setup`.
 *   - `edit`    — editing an existing blueprint's settings (label, included
 *                 object types, included schemas, excluded objects). Driven
 *                 from the Database Blueprint toolbar's Settings button.
 *
 * Any surface that wants to open the dialog (Object Explorer DB context menu,
 * command palette, main-toolbar More menu) calls `openSetupDialog` for the
 * new-blueprint flow. The toolbar Settings button calls `openSettingsDialog`.
 */

import { create } from 'zustand';
import type { BlueprintMetadata, UserFacingEngineId } from '../types/databaseBlueprintTypes';

export type DatabaseBlueprintSetupSource = {
  connectionId: string;
  connectionName: string;
  database: string;
  engine: UserFacingEngineId;
};

export type DatabaseBlueprintSetupMode = 'new' | 'edit';

export type DatabaseBlueprintEditContext = {
  /** Blueprint folder path on disk. */
  blueprintFolderPath: string;
  /** Folder id in the script-browser store (used to push updates back). */
  blueprintFolderId: string;
  /** Snapshot of the on-disk `jamsql-blueprint.json` to pre-populate fields. */
  metadata: BlueprintMetadata;
};

type State = {
  isOpen: boolean;
  mode: DatabaseBlueprintSetupMode;
  source: DatabaseBlueprintSetupSource | null;
  editContext: DatabaseBlueprintEditContext | null;
};

type Actions = {
  openSetupDialog: (source: DatabaseBlueprintSetupSource) => void;
  openSettingsDialog: (
    source: DatabaseBlueprintSetupSource,
    edit: DatabaseBlueprintEditContext,
  ) => void;
  closeSetupDialog: () => void;
};

export const useDatabaseBlueprintSetupStore = create<State & Actions>((set) => ({
  isOpen: false,
  mode: 'new',
  source: null,
  editContext: null,
  openSetupDialog: (source) =>
    set({ isOpen: true, mode: 'new', source, editContext: null }),
  openSettingsDialog: (source, edit) =>
    set({ isOpen: true, mode: 'edit', source, editContext: edit }),
  closeSetupDialog: () =>
    set({ isOpen: false, mode: 'new', source: null, editContext: null }),
}));
